import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@ellucian/ds-icons/lib';
import CheckFeedback from '@ellucian/ds-icons/lib/CheckFeedback';
import Clock from '@ellucian/ds-icons/lib/Clock';
import Spinner from '@ellucian/ds-icons/lib/Spinner';
import Warning from '@ellucian/ds-icons/lib/Warning';
import { Box, CircularProgress, IconButton, Pagination, StatusLabel, Typography, Tooltip } from '@ellucian/react-design-system/core';
import AdvancedTable from '@ellucian/react-design-system/core/AdvancedTable';
import Paper from '@ellucian/react-design-system/core/Paper';
import Drawer from '@mui/material/Drawer';
import { appConfig } from '../../utils/config';

interface InstallStatusRow {
    tenantId?: string;
    initiatedBy?: string;
    startTime?: string;
    updatedAt?: string;
    status?: string;
    errorMessage?: string;
}

interface InstallStatusDrawerProps {
    open: boolean;
    onClose: () => void;
    solutionName: string;
    tenantId?: string;
    integrationId?: string;
    erpType?: string;
    statuses?: InstallStatusRow[];
}

const getTenantName = (tenantId?: string): string => {
    if (!tenantId) return '-';

    try {
        const tenants = JSON.parse(localStorage.getItem('tenantsData') || '[]');
        const tenant = Array.isArray(tenants)
            ? tenants.find((item) => item?.id === tenantId)
            : null;
        return tenant?.label || tenant?.name || tenant?.alias || '-';
    } catch {
        return '-';
    }
};

const formatInstallDate = (value?: string): string => {
    if (!value) return '-';

    const parsedDate = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
    if (Number.isNaN(parsedDate.getTime())) return value;

    const parts = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).formatToParts(parsedDate);
    const values = parts.reduce<Record<string, string>>((result, part) => {
        result[part.type] = part.value;
        return result;
    }, {});

    return `${values.month} ${values.day}, ${values.year} at ${values.hour}:${values.minute} ${values.dayPeriod}`;
};

const InstallStatusDrawer: React.FC<InstallStatusDrawerProps> = ({
    open,
    onClose,
    solutionName,
    tenantId = '',
    integrationId = '',
    erpType = '',
    statuses = [],
}) => {
    const [installStatuses, setInstallStatuses] = useState<InstallStatusRow[]>(statuses);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
    const [sortState, setSortState] = useState<{ id: string; desc: boolean }>({ id: 'startTime', desc: true });
    const [refreshKey, setRefreshKey] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const recordsPerPage = 10;
    const installStatusesRef = useRef(installStatuses);
    installStatusesRef.current = installStatuses;

    const toggleSort = (columnId: string) => {
        setSortState((current) => (
            current.id === columnId
                ? { id: columnId, desc: !current.desc }
                : { id: columnId, desc: true }
        ));
    };

    const renderSortableHeader = (label: string, columnId: string) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>{label}</span>
            <IconButton
                aria-label={`Sort by ${label}`}
                title={`Sort by ${label}`}
                onClick={(event: any) => {
                    event.stopPropagation();
                    toggleSort(columnId);
                }}
                sx={{
                    padding: '2px',
                    backgroundColor: 'transparent !important',
                    border: 'none !important',
                    boxShadow: 'none !important',
                    outline: 'none !important',
                    '&:hover': {
                        backgroundColor: 'transparent !important',
                        boxShadow: 'none !important',
                    },
                    '&:focus, &.Mui-focusVisible': {
                        backgroundColor: 'transparent !important',
                        boxShadow: 'none !important',
                        outline: 'none !important',
                    },
                    '&:active': {
                        backgroundColor: '#E5E7E9 !important',
                        boxShadow: 'none !important',
                        outline: 'none !important',
                    },
                }}
            >
                <Icon name="arrow-down-arrow-up" style={{ width: 16, height: 16, color: '#5B5E65' }} />
            </IconButton>
        </Box>
    );

    useEffect(() => {
        if (!open) return;

        const fetchInstallStatuses = async () => {
            setLoading(true);
            setError('');

            try {
                const storedTenants = JSON.parse(localStorage.getItem('tenantsData') || '[]');
                const tenants = Array.isArray(storedTenants)
                    ? storedTenants.filter((tenant) => tenant?.id)
                    : [];

                const resolvedErpType = erpType.trim();
                if (!resolvedErpType) {
                    setInstallStatuses([]);
                    console.log('ERP type is not available yet. Please try again shortly.');
                    setError('ERP type is not available yet. Please try again shortly.');
                    return;
                }

                const isRefresh = refreshKey > 0;
                const terminalTenantIds = new Set(
                    isRefresh
                        ? installStatusesRef.current
                            .filter((row) => ['completed', 'failed'].includes((row.status || '').toLowerCase()))
                            .map((row) => row.tenantId)
                        : []
                );
                const tenantsToRefresh = tenants.filter((tenant) => !terminalTenantIds.has(tenant.id));

                const tenantRequests = tenantsToRefresh.map(async (tenant) => {
                    const response = await fetch(`${appConfig.api.baseUrl}/api/jobs/install-status`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': 'cookie-based-auth'
                        },
                        credentials: 'include',
                        body: JSON.stringify({
                            tenantId: tenant.id,
                            integrationId,
                            erpType: resolvedErpType,
                        }),
                    });

                    // Handle 404 - no installation found for this tenant, skip it
                    if (response.status === 404) {
                        return [];
                    }

                    // Handle 503 - service unavailable
                    if (response.status === 503) {
                        throw new Error('SERVICE_UNAVAILABLE');
                    }

                    // Handle other non-ok responses
                    if (!response.ok) {
                        throw new Error(`Unable to load installation status for tenant ${tenant.id} (${response.status})`);
                    }

                    // 200 OK - process response (including those with errorMessage)
                    const responseData = await response.json();
                    const rows = Array.isArray(responseData)
                        ? responseData
                        : responseData?.statuses || responseData?.jobs || responseData?.data
                            || (responseData?.tenantId ? [responseData] : []);

                    return (Array.isArray(rows) ? rows : []).map((row) => ({
                        ...row,
                        tenantId: row.tenantId || tenant.id,
                    }));
                });

                const results = await Promise.allSettled(tenantRequests);
                
                // Check for service unavailable errors
                const serviceUnavailableResult = results.find(
                    (result) => result.status === 'rejected' && result.reason instanceof Error && result.reason.message === 'SERVICE_UNAVAILABLE'
                );

                if (serviceUnavailableResult) {
                    setInstallStatuses([]);
                    setError('Installation status is temporarily unavailable');
                } else {
                    const successfulRows = results
                        .filter((result): result is PromiseFulfilledResult<InstallStatusRow[]> => result.status === 'fulfilled')
                        .flatMap((result) => result.value);

                    const terminalRows = isRefresh
                        ? installStatuses.filter((row) => terminalTenantIds.has(row.tenantId))
                        : [];
                    
                    const allRows = [...terminalRows, ...successfulRows];
                    setInstallStatuses(allRows);
                    
                    // If no rows at all (all 404s or no tenants), don't set error - the UI will show "No installation activity yet"
                    if (allRows.length === 0 && tenantsToRefresh.length > 0) {
                        setError('');
                    }
                }
                setExpandedRows({});
            } catch (requestError) {
                setInstallStatuses([]);
                setError(requestError instanceof Error ? requestError.message : 'Unable to load installation status.');
            } finally {
                setLoading(false);
            }
        };

        fetchInstallStatuses();
    }, [open, tenantId, integrationId, erpType, refreshKey]);

    const normalizedStatuses = installStatuses.map((row) => ({
        ...row,
        status: row.status || 'Queued',
        tenant: getTenantName(row.tenantId),
    }));

    const sortedStatuses = useMemo(() => {
        const { id, desc } = sortState;
        const sorted = [...normalizedStatuses].sort((a: any, b: any) =>
            String(a[id] ?? '').localeCompare(String(b[id] ?? ''))
        );
        return desc ? sorted.reverse() : sorted;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [installStatuses, sortState]);

    const paginatedStatuses = sortedStatuses.slice(
        currentPage * recordsPerPage,
        (currentPage + 1) * recordsPerPage
    );

    useEffect(() => {
        const lastPage = Math.max(0, Math.ceil(sortedStatuses.length / recordsPerPage) - 1);
        setCurrentPage((page) => Math.min(page, lastPage));
    }, [sortedStatuses.length]);

    const columns = useMemo(() => [
        {
            accessorKey: 'tenant',
            header: 'Tenant',
            editType: 'text',
            size: 140,
            minSize: 140,
            maxSize: 140,
            Header: () => renderSortableHeader('Tenant', 'tenant'),
            Cell: ({ cell }: { cell: { getValue: () => string } }) => (
                <span style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }} key="tenant-cell">{cell.getValue() || '-'}</span>
            ),
            muiTableBodyCellProps: { sx: { whiteSpace: 'normal', overflowWrap: 'anywhere' } },
        },
        {
            accessorKey: 'tenantId',
            header: 'Tenant ID',
            editType: 'text',
            minSize: 320,
            size: 320,
            maxSize: 320,
            Header: () => renderSortableHeader('Tenant ID', 'tenantId'),
            Cell: ({ cell }: { cell: { getValue: () => string } }) => (
                <span style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }} key="tenantId-cell">{cell.getValue() || '-'}</span>
            ),
            muiTableHeadCellProps: { sx: { whiteSpace: 'nowrap' } },
            muiTableBodyCellProps: { sx: { whiteSpace: 'normal', overflowWrap: 'anywhere' } },
        },
        {
            accessorKey: 'startTime',
            header: 'Install Started On',
            editType: 'text',
            size: 230,
            minSize: 230,
            maxSize: 230,
            Header: () => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                    <span>Install Started On</span>
                    <IconButton
                        aria-label="Sort by Install Started On"
                        title="Sort by Install Started On"
                        onClick={(event: any) => {
                            event.stopPropagation();
                            toggleSort('startTime');
                        }}
                        sx={{
                            padding: '2px',
                            backgroundColor: 'transparent !important',
                            border: 'none !important',
                            boxShadow: 'none !important',
                            outline: 'none !important',
                            '&:hover': { backgroundColor: 'transparent !important', boxShadow: 'none !important' },
                            '&:focus, &.Mui-focusVisible': {
                                backgroundColor: 'transparent !important',
                                boxShadow: 'none !important',
                                outline: 'none !important',
                            },
                            '&:active': {
                                backgroundColor: '#E5E7E9 !important',
                                boxShadow: 'none !important',
                                outline: 'none !important',
                            },
                        }}
                    >
                        <Icon name="arrow-down-arrow-up" style={{ width: 16, height: 16, color: '#5B5E65' }} />
                    </IconButton>
                </Box>
            ),
            Cell: ({ cell }: { cell: { getValue: () => string } }) => formatInstallDate(cell.getValue()),
            muiTableBodyCellProps: { sx: { whiteSpace: 'normal', overflowWrap: 'anywhere' } },
        },
        {
            accessorKey: 'initiatedBy',
            header: 'Installed By',
            editType: 'text',
            size: 190,
            minSize: 190,
            maxSize: 190,
            Header: () => renderSortableHeader('Installed By', 'initiatedBy'),
            muiTableBodyCellProps: { sx: { whiteSpace: 'normal', overflowWrap: 'anywhere' } },
        },
        {
            accessorKey: 'status',
            header: 'Status',
            editType: 'text',
            minSize: 149,
            size: 149,
            maxSize: 149,
            Header: () => renderSortableHeader('Status', 'status'),
            Cell: ({ cell }: { cell: { getValue: () => string } }) => {
                const status = cell.getValue() || 'Queued';
                const normalizedStatus = status.toLowerCase();
                const statusType = status.toLowerCase() === 'completed'
                    ? 'success'
                    : status.toLowerCase() === 'failed'
                        ? 'error'
                        : 'default';
                const statusIcon = normalizedStatus === 'completed'
                    ? <CheckFeedback />
                    : normalizedStatus === 'failed'
                        ? <Warning />
                        : normalizedStatus === 'in progress'
                            ? <Spinner />
                            : <Clock />;
                return (
                    <span style={{ display: 'inline-flex', minWidth: 130, whiteSpace: 'nowrap' }}>
                        <StatusLabel
                            id={`installStatus_${normalizedStatus}`}
                            text={status.toUpperCase()}
                            type={statusType}
                            icon={statusIcon}
                            sx={normalizedStatus === 'in progress' ? {
                                color: '#2874BB !important',
                                border: '1px solid #2874BB !important',
                                fill: '#2874BB !important',
                                '& svg': {
                                    color: '#2874BB !important',
                                    fill: '#2874BB !important',
                                },
                            } : undefined}
                        />
                    </span>
                );
            },
            muiTableHeadCellProps: { sx: { whiteSpace: 'nowrap' } },
            muiTableBodyCellProps: { sx: { whiteSpace: 'nowrap' } },
        },
    ], [sortState]);

    return (
        <Drawer
            id="install-status-drawer"
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                component: 'section',
                'aria-label': 'Install Status',
                sx: {
                    width: '1156px',
                    maxWidth: '100vw',
                    height: { xs: '100vh', md: 'min(1024px, 100vh)' },
                    boxSizing: 'border-box',
                    overflow: { xs: 'auto', md: 'hidden' },
                },
            }}
        >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 24, boxSizing: 'border-box', minHeight: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div>
                            <h2 style={{ margin: 0, color: '#151618', fontFamily: 'Poppins, sans-serif', fontSize: 24, lineHeight: '32px' }}>
                                Install Status
                            </h2>
                            <p style={{ margin: '2px 0 0', color: '#5B5E65', fontSize: 14, lineHeight: '20px' }}>
                                Listing <strong>{solutionName}</strong>
                            </p>
                        </div>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Tooltip title="Close Install Status">
                                <IconButton 
                                    color="gray" 
                                    aria-label="Close Install Status"
                                    title="Close Install Status"
                                    onClick={onClose}>
                                    <Icon name="close" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </div>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, minHeight: 40 }}>
                        <Typography sx={{ color: '#5B5E65' }}>
                            Note: This page displays the status of the most recent installation. For a full audit trail of installation activity, see the <strong>Ethos Audit Logs</strong>.
                        </Typography>
                        <Tooltip title="Refresh Install Status">
                            <IconButton 
                                color="secondary" 
                                aria-label="Refresh Install Status" 
                                title="Refresh Install Status"
                                onClick={() => setRefreshKey((current) => current + 1)}>
                                <Icon name="refresh" />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Box sx={{ overflowX: { xs: 'auto', md: 'hidden' }, width: '100%' }}>
                        {loading && (
                            <div style={{ margin: '32px 0', color: '#5B5E65', textAlign: 'center' }}>
                                <p style={{ margin: 0 }}>Loading installation status...</p>
                                <CircularProgress
                                    size={32}
                                    aria-label="Loading installation status"
                                    aria-valuetext="Loading installation status"
                                    style={{ marginTop: 8 }}
                                />
                            </div>
                        )}
                        {!loading && error && (
                            <p style={{ margin: '16px 0', color: sortedStatuses.length > 0 ? '#8A5A00' : '#D61F2C', textAlign: 'center' }}>{error}</p>
                        )}
                        {!loading && sortedStatuses.length > 0 && (
                            <>
                                <Paper sx={{ p: 0, width: '100%', minWidth: { xs: 1108, md: 0 } }}>
                                    <AdvancedTable
                                    data={paginatedStatuses}
                                    columns={columns}
                                    enableExpanding={true}
                                    enableExpandAll={true}
                                    enableSorting={false}
                                    enablePagination={false}
                                    enableDensityToggle={false}
                                    enableFullScreenToggle={false}
                                    enableHiding={false}
                                    enableFilters={false}
                                    enableColumnFilters={false}
                                    enableGlobalFilter={false}
                                    enableTopToolbar={false}
                                    enableColumnActions={false}
                                    getRowCanExpand={({ original }: any) => original.status?.toLowerCase() === 'failed'}
                                    displayColumnDefOptions={{
                                        'mrt-row-expand': {
                                            muiTableHeadCellProps: {
                                                sx: {
                                                    order: 6,
                                                    '& button, & button svg': { color: '#5B5E65', fill: '#5B5E65' },
                                                },
                                            },
                                            muiTableBodyCellProps: ({ row }: any) => (
                                                row.original.status?.toLowerCase() === 'failed'
                                                    ? {
                                                        sx: {
                                                            order: 6,
                                                            '& button, & button svg': { color: '#5B5E65', fill: '#5B5E65' },
                                                        },
                                                    }
                                                    : { sx: { order: 6, '& button': { visibility: 'hidden' } } }
                                            ),
                                        },
                                    }}
                                    expandedRows={expandedRows}
                                    onExpandedChange={setExpandedRows}
                                    renderDetailPanel={({ row }: any) => {
                                        return row.original.status?.toLowerCase() === 'failed' ? (
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    margin: 'auto',
                                                    gridTemplateColumns: '1fr 1fr',
                                                    width: '100%',
                                                }}
                                            >
                                                <Typography>{row.original.errorMessage || '-'}</Typography>
                                            </Box>
                                        ) : null;
                                    }}
                                    />
                                </Paper>
                            </>
                        )}
                        {!loading && !error && sortedStatuses.length > recordsPerPage && (
                            <Pagination
                                count={sortedStatuses.length}
                                page={currentPage}
                                rowsPerPage={recordsPerPage}
                                rowsPerPageOptions={[recordsPerPage]}
                                onPageChange={(_: unknown, page: number) => setCurrentPage(page)}
                                sx={{ mt: 1 }}
                            />
                        )}
                        {!loading && !error && normalizedStatuses.length === 0 && (
                            <p style={{ margin: '32px 0', color: '#5B5E65', textAlign: 'center' }}>No installation activity yet.</p>
                        )}
                    </Box>
                </div>
        </Drawer>
    );
};

export default InstallStatusDrawer;